import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { createRoot, Root } from 'react-dom/client';
import { TextField, Button, Box, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { useClickOutside } from '../../../hooks/use-common-actions';
import { convertToHalf } from '../../common/component-editor-class';

import type Handsontable from 'handsontable';

import { EPlanFormulationProductTypeLabel } from '@/features/plans/types/plan.enum';

import './merchandise-styles.css';

interface MerchandiseForm {
  fcInput: string;
  sInput: string;
  fmInput: string;
  slSfInput: string;
  gInput: string;
  cementInput: string;
  adRadio: 'AE' | 'SPC';
  admixtureInput: string;
  miscInput: string;
}

interface MerchandisePopupProps {
  instance: Handsontable.Core;
  row: number;
  col: number;
  cell: HTMLElement;
  value: string;
  onClose: () => void;
}

const SPEC_DEFAULT = '18-20N';
const INIT_SPEC: MerchandiseForm = {
  fcInput: '',
  sInput: '',
  fmInput: '',
  slSfInput: '18',
  gInput: '20',
  cementInput: 'N',
  adRadio: 'AE',
  admixtureInput: '',
  miscInput: '',
};

const SL_SF_ARR = ['15', '18', '21'];
const G_ARR = ['15', '20', '25', '40'];
const CEMENT_ARR = ['N', 'BB', 'H', 'M'];
const NUMBER_HALF_WIDTH_REGEX = /[^0-9０-９.．]/g;

const POPUP_VERTICAL_GAP_PX = 4;
const POPUP_Z_INDEX = '10000';

/**
 * Handsontable and layout wrappers use nested scroll containers; grid scroll often does not
 * bubble to window, so we listen on scroll parents (including .wtHolder) explicitly.
 */
function getScrollableAncestors(element: HTMLElement | null): HTMLElement[] {
  const list: HTMLElement[] = [];
  const seen = new Set<HTMLElement>();

  function push(node: HTMLElement): void {
    if (!seen.has(node)) {
      seen.add(node);
      list.push(node);
    }
  }

  const wtHolder = element?.closest?.('.wtHolder') as HTMLElement | null;
  if (wtHolder) push(wtHolder);

  let node: HTMLElement | null = element?.parentElement ?? null;
  while (node && node !== document.body) {
    const style = window.getComputedStyle(node);
    const scrollableY =
      /(auto|scroll|overlay)/.test(style.overflowY) && node.scrollHeight > node.clientHeight;
    const scrollableX =
      /(auto|scroll|overlay)/.test(style.overflowX) && node.scrollWidth > node.clientWidth;

    if (scrollableY || scrollableX) push(node);

    node = node.parentElement;
  }

  return list;
}

/**
 * Merchandise popup component for Handsontable cells
 * Provides a form interface for inputting merchandise specifications
 */
const MerchandisePopup: React.FC<MerchandisePopupProps> = ({
  instance,
  row,
  col,
  cell,
  value,
  onClose,
}) => {
  const popupRef = useRef<HTMLDivElement>(null);
  const isProcessingInputRef = useRef<boolean>(false);

  const [form, setForm] = useState<MerchandiseForm>(() => {
    if (value) {
      const parsed = convertSpec(value);
      return parsed;
    }
    return { ...INIT_SPEC };
  });

  const [specInput, setSpecInput] = useState<string>(() => {
    if (value) {
      return value;
    }
    return SPEC_DEFAULT;
  });

  const [fmIsCalculated, setFmIsCalculated] = useState<boolean>(false);
  const [popupPosition, setPopupPosition] = useState<{ top: number; left: number } | null>(null);

  // Match autocomplete behavior: render below by default and flip above if there isn't enough space.
  useEffect(() => {
    if (!popupRef.current) return;

    let rafQueued = false;

    function applyPosition(): void {
      if (!popupRef.current || !document.documentElement.contains(cell)) return;

      const cellRect = cell.getBoundingClientRect();
      const popupHeight = popupRef.current.offsetHeight || 300;
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - cellRect.bottom;
      const spaceAbove = cellRect.top;
      const shouldPositionAbove =
        spaceBelow < popupHeight + POPUP_VERTICAL_GAP_PX && spaceAbove > popupHeight;

      const top = shouldPositionAbove
        ? cellRect.top - popupHeight - POPUP_VERTICAL_GAP_PX
        : cellRect.bottom + POPUP_VERTICAL_GAP_PX;

      setPopupPosition({
        top,
        left: cellRect.right,
      });
    }

    function schedulePosition(): void {
      if (rafQueued) return;
      rafQueued = true;
      window.requestAnimationFrame(() => {
        rafQueued = false;
        applyPosition();
      });
    }

    const rafInitial = window.requestAnimationFrame(() => {
      applyPosition();
      window.requestAnimationFrame(applyPosition);
    });

    window.addEventListener('resize', schedulePosition, { passive: true });
    window.addEventListener('scroll', schedulePosition, true);

    const scrollParents = getScrollableAncestors(cell);
    for (const parent of scrollParents) {
      parent.addEventListener('scroll', schedulePosition, { passive: true });
    }

    let removeAfterScroll: (() => void) | undefined;
    if (
      instance &&
      typeof instance.addHook === 'function' &&
      typeof instance.removeHook === 'function'
    ) {
      instance.addHook('afterScroll', schedulePosition);
      removeAfterScroll = () => instance.removeHook('afterScroll', schedulePosition);
    }

    return () => {
      window.cancelAnimationFrame(rafInitial);
      window.removeEventListener('resize', schedulePosition);
      window.removeEventListener('scroll', schedulePosition, true);
      scrollParents.forEach((p) => {
        p.removeEventListener('scroll', schedulePosition);
      });
      removeAfterScroll?.();
    };
  }, [cell, instance]);

  // Close on mousedown (capture) so other cell editors don't start before this popup closes.
  useClickOutside(popupRef, cell, () => handleClose(false), 'mousedown');

  /**
   * Convert spec string to form values
   */
  function convertSpec(specString: string): MerchandiseForm {
    const formData = { ...INIT_SPEC };
    const platArr = specString.split(' ');
    const platSymbolDash = platArr[0].split('-');

    // Check admixture and other
    if (platArr.length >= 3) {
      formData.admixtureInput = platArr[platArr.length - 2];
      formData.miscInput = platArr[platArr.length - 1];
    }

    // Check ad
    if (platArr.includes('SPC')) {
      formData.adRadio = 'SPC';
    } else {
      formData.adRadio = 'AE';
    }

    // Check Fc
    const lastIndex = platSymbolDash[0].indexOf(')');
    if (platSymbolDash[0].includes('Fc')) {
      formData.fcInput = platSymbolDash[0].slice(3, lastIndex);
      formData.fmInput = platSymbolDash[0].slice(lastIndex + 1);
      const fc = parseFloat(formData.fcInput) || 0;
      const fm = parseFloat(formData.fmInput) || 0;
      formData.sInput = (fm - fc).toString();
    } else {
      formData.fcInput = '';
      formData.sInput = '';
    }

    // Check s, sfsl, g, cement
    if (platSymbolDash.length === 3) {
      formData.fmInput = platSymbolDash[0].slice(lastIndex + 1);
      formData.slSfInput = platSymbolDash[1];
      formData.cementInput = CEMENT_ARR.find((ce) => platSymbolDash[2].includes(ce)) || '';
      formData.gInput = G_ARR.find((g) => platSymbolDash[2].includes(g)) || '';
    } else {
      formData.slSfInput = SL_SF_ARR.find((slSf) => platSymbolDash.includes(slSf)) || '';
      if (!formData.slSfInput && platSymbolDash[0].includes('Fc') && platSymbolDash.length === 3) {
        formData.slSfInput = platSymbolDash[1];
      }
    }

    if (platSymbolDash.length === 2) {
      formData.fmInput = !isNaN(Number(platSymbolDash[0])) ? platSymbolDash[0] : '';
      formData.cementInput = CEMENT_ARR.find((ce) => platSymbolDash[1].includes(ce)) || '';
      formData.gInput = G_ARR.find((g) => platSymbolDash[1].includes(g)) || '';
    }

    return formData;
  }

  /**
   * Check if a value is a valid number
   */
  const isValidValue = useCallback((value: string): boolean => {
    return !isNaN(Number(value)) && Boolean(value) && value.trim() !== '';
  }, []);

  /**
   * Determine if (Fc) prefix should be shown
   */
  const shouldShowFcPrefix = useCallback(
    (hasValidFc: boolean, hasValidFm: boolean, hasValidS: boolean): boolean => {
      // Show (Fc) if:
      // 1. User entered Fc explicitly (hasValidFc) - show (Fc{fcInput})
      // 2. User entered Fm without Fc (hasValidFm && !hasValidFc) - default Fc = 0, show (Fc)
      // 3. User entered S without Fc (hasValidS && !hasValidFc) - default Fc = 0, show (Fc)
      return hasValidFc || (hasValidFm && !hasValidFc) || (hasValidS && !hasValidFc);
    },
    []
  );

  /**
   * Build the (Fc) prefix string
   */
  const buildFcPrefix = useCallback(
    (fcInput: string, shouldShow: boolean, hasValidFc: boolean): string => {
      if (!shouldShow) return '';
      // If shouldShowFc but no explicit Fc value, use empty (Fc) to represent Fc = 0
      return hasValidFc ? `(Fc${fcInput})` : '(Fc)';
    },
    []
  );

  /**
   * Build additional parts of the spec string
   */
  const buildAdditionalParts = useCallback(
    (
      adRadio: 'AE' | 'SPC',
      admixtureInput: string,
      miscInput: string
    ): { adRadioShow: string; admixtureShow: string; miscShow: string } => {
      return {
        adRadioShow: adRadio !== 'AE' ? ' ' + adRadio : '',
        admixtureShow: admixtureInput ? ' ' + admixtureInput : '',
        miscShow: miscInput ? ' ' + miscInput : '',
      };
    },
    []
  );

  /**
   * Update spec input string from form values
   */
  const updateSpecInput = useCallback(() => {
    const {
      fcInput,
      sInput,
      fmInput,
      slSfInput,
      gInput,
      cementInput,
      adRadio,
      admixtureInput,
      miscInput,
    } = form;

    const hasValidFc = isValidValue(fcInput);
    const hasValidS = isValidValue(sInput);
    const hasValidFm = isValidValue(fmInput);

    const shouldShow = shouldShowFcPrefix(hasValidFc, hasValidFm, hasValidS);
    const fcShow = buildFcPrefix(fcInput, shouldShow, hasValidFc);

    const fmShow = fmInput ? fmInput + '-' : '';
    const slSfShow = slSfInput ? slSfInput + '-' : '';
    const gShow = (gInput || '') + (cementInput || '');

    const { adRadioShow, admixtureShow, miscShow } = buildAdditionalParts(
      adRadio,
      admixtureInput,
      miscInput
    );

    let result = fcShow + fmShow + slSfShow + gShow;
    if (adRadioShow) result += adRadioShow;
    if (admixtureShow) result += admixtureShow;
    if (miscShow) result += miscShow;

    setSpecInput(result.trim());
  }, [form, fmIsCalculated, isValidValue, shouldShowFcPrefix, buildFcPrefix, buildAdditionalParts]);

  // Update spec input whenever form changes
  useEffect(() => {
    updateSpecInput();
  }, [form, fmIsCalculated, updateSpecInput]);

  /**
   * Clean number input value
   */
  const cleanNumberInput = useCallback((rawValue: string): string => {
    const newVal = rawValue.replaceAll(NUMBER_HALF_WIDTH_REGEX, '');
    return convertToHalf(newVal);
  }, []);

  /**
   * Check if a number value is valid
   */
  const isValidNumber = useCallback((value: string | undefined | null): boolean => {
    if (value === undefined || value === null || value.trim() === '') {
      return false;
    }
    const num = parseFloat(value);
    return !isNaN(num);
  }, []);

  /**
   * Handle Fm input change - clear Fc and S if Fm is entered directly
   */
  const handleFmInputChange = useCallback(
    (cleanedValue: string, prevForm: MerchandiseForm, updated: MerchandiseForm) => {
      if (!cleanedValue) {
        setFmIsCalculated(false);
        return;
      }

      const currentFc = parseFloat(prevForm.fcInput) || 0;
      const currentS = parseFloat(prevForm.sInput) || 0;
      const calculatedFm = currentFc && currentS ? (currentFc + currentS).toString() : '';

      if (cleanedValue !== calculatedFm) {
        updated.fcInput = '';
        updated.sInput = '';
        setFmIsCalculated(false);
      } else if (calculatedFm && cleanedValue === calculatedFm) {
        setFmIsCalculated(true);
      } else {
        setFmIsCalculated(false);
      }
    },
    []
  );

  /**
   * Handle Fc or S input change - calculate Fm automatically
   * Fm = Fc + S (use 0 as default for missing value)
   */
  const handleFcOrSInputChange = useCallback(
    (
      name: 'fcInput' | 'sInput',
      cleanedValue: string,
      prevForm: MerchandiseForm,
      updated: MerchandiseForm
    ) => {
      const fcValue = name === 'fcInput' ? cleanedValue : prevForm.fcInput;
      const sValue = name === 'sInput' ? cleanedValue : prevForm.sInput;

      const hasValidFc = isValidNumber(fcValue);
      const hasValidS = isValidNumber(sValue);

      // Calculate Fm if at least one value is valid
      // Use 0 as default for missing value (not displayed in UI)
      if (hasValidFc || hasValidS) {
        const fcNum = hasValidFc ? parseFloat(fcValue) : 0;
        const sNum = hasValidS ? parseFloat(sValue) : 0;
        const calculatedFm = (fcNum + sNum).toString();
        updated.fmInput = calculatedFm;
        setFmIsCalculated(hasValidFc && hasValidS);
      } else {
        // Both are empty, clear Fm
        updated.fmInput = '';
        setFmIsCalculated(false);
      }
    },
    [isValidNumber, fmIsCalculated]
  );

  /**
   * Process number input with validation and auto-calculation
   */
  const processNumberInput = useCallback(
    (name: 'fcInput' | 'sInput' | 'fmInput', rawValue: string) => {
      if (isProcessingInputRef.current) {
        return;
      }
      isProcessingInputRef.current = true;

      try {
        const cleanedValue = cleanNumberInput(rawValue);

        setForm((prev) => {
          const updated = { ...prev, [name]: cleanedValue };

          if (name === 'fmInput') {
            handleFmInputChange(cleanedValue, prev, updated);
          } else if (name === 'fcInput' || name === 'sInput') {
            handleFcOrSInputChange(name, cleanedValue, prev, updated);
          }

          return updated;
        });
      } finally {
        isProcessingInputRef.current = false;
      }
    },
    [cleanNumberInput, handleFmInputChange, handleFcOrSInputChange]
  );

  /**
   * Handle input with dropdown
   */
  const onlyHalfWidthNumber = useCallback((name: 'slSfInput' | 'gInput', rawValue: string) => {
    const newVal = rawValue.replaceAll(NUMBER_HALF_WIDTH_REGEX, '');
    const cleanedValue = convertToHalf(newVal);
    setForm((prev) => ({ ...prev, [name]: cleanedValue }));
  }, []);

  /**
   * Handle cement input
   */
  const onlyHalfWidthString = useCallback((rawValue: string) => {
    const newVal = rawValue.replaceAll(/[^a-zA-Z]/g, '');
    const cleanedValue = convertToHalf(newVal).toUpperCase();
    setForm((prev) => ({ ...prev, cementInput: cleanedValue }));
  }, []);

  /**
   * Handle form value update
   */
  const updateFormValue = useCallback((name: keyof MerchandiseForm, value: string) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  }, []);

  /**
   * Handle option selection from dropdown
   */
  const handleSelect = useCallback(
    (name: keyof MerchandiseForm, value: string) => {
      if (name === 'sInput') {
        processNumberInput(name, value);
      } else {
        updateFormValue(name, value);
      }
    },
    [processNumberInput, updateFormValue]
  );

  /**
   * Handle input change
   */
  const handleInputChange = useCallback(
    (name: keyof MerchandiseForm, value: string, type: 'number' | 'text') => {
      if (type === 'number') {
        if (name === 'fcInput' || name === 'sInput' || name === 'fmInput') {
          processNumberInput(name, value);
        } else if (name === 'slSfInput' || name === 'gInput') {
          onlyHalfWidthNumber(name, value);
        } else {
          updateFormValue(name, value);
        }
      } else if (name === 'cementInput') {
        onlyHalfWidthString(value);
      } else {
        updateFormValue(name, value);
      }
    },
    [processNumberInput, onlyHalfWidthNumber, onlyHalfWidthString, updateFormValue]
  );

  /**
   * Handle close
   */
  const handleClose = useCallback(
    (save: boolean) => {
      if (save) {
        markMerchandiseSessionCommitted();
        instance.setDataAtCell(row, col, specInput);
        instance.render();
      }
      onClose();
      if (save) {
        // MUI 決定 removes focus from the grid; restore selection so Enter / keyboard flow continues.
        globalThis.requestAnimationFrame(() => {
          globalThis.requestAnimationFrame(() => {
            try {
              if (instance?.isDestroyed) {
                return;
              }
              instance.selectCell(row, col);
              if (typeof instance.listen === 'function') {
                instance.listen();
              }
            } catch {
              /* instance may be gone */
            }
          });
        });
      }
    },
    [instance, row, col, specInput, onClose]
  );

  /**
   * Handle submit
   */
  const handleSubmit = useCallback(() => {
    handleClose(true);
  }, [handleClose]);

  /**
   * Get input order array
   */
  const getInputOrder = useCallback((): (keyof MerchandiseForm)[] => {
    return [
      'fcInput',
      'sInput',
      'fmInput',
      'slSfInput',
      'gInput',
      'cementInput',
      'admixtureInput',
      'miscInput',
    ];
  }, []);

  /**
   * Get next input name based on current input name
   */
  const getNextInputName = useCallback(
    (currentName: keyof MerchandiseForm): keyof MerchandiseForm | null => {
      const inputs = getInputOrder();
      const currentIndex = inputs.indexOf(currentName);

      if (currentName === 'cementInput' && inputs[currentIndex + 3]) {
        return inputs[currentIndex + 3];
      }

      if (currentIndex < inputs.length - 1) {
        return inputs[currentIndex + 1];
      }

      return null;
    },
    [getInputOrder]
  );

  /**
   * Focus on next input element
   */
  const focusNextInput = useCallback((nextInputName: keyof MerchandiseForm) => {
    const nextInput = popupRef.current?.querySelector(
      `input[name="${nextInputName}"]`
    ) as HTMLInputElement;
    if (nextInput) {
      nextInput.focus();
    }
  }, []);

  /**
   * Handle Enter key press
   */
  const handleEnterKey = useCallback(
    (name: keyof MerchandiseForm) => {
      if (name === 'miscInput') {
        handleSubmit();
        return;
      }

      const nextInputName = getNextInputName(name);
      if (nextInputName) {
        focusNextInput(nextInputName);
      }
    },
    [handleSubmit, getNextInputName, focusNextInput]
  );

  /**
   * Handle keyboard navigation
   */
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, name: keyof MerchandiseForm) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleEnterKey(name);
      }
    },
    [handleEnterKey]
  );

  /**
   * Handle clear
   */
  const handleClear = useCallback(() => {
    setForm({ ...INIT_SPEC });
    setSpecInput(SPEC_DEFAULT);
    setFmIsCalculated(false);
  }, []);

  return createPortal(
    <Box
      ref={popupRef}
      className='merchandise-popup'
      sx={{
        position: 'fixed',
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 4px 8px 3px rgba(0, 0, 0, 0.15)',
        padding: '12px',
        width: 'auto',
        fontFamily: 'Arial, sans-serif',
        fontSize: '12px',
        zIndex: POPUP_Z_INDEX,
        transform: 'translateX(-100%)',
        ...(popupPosition
          ? {
              top: `${popupPosition.top}px`,
              left: `${popupPosition.left}px`,
            }
          : {
              top: '-9999px',
              left: '-9999px',
            }),
      }}
    >
      <IconButton
        onClick={() => handleClose(false)}
        sx={{
          position: 'absolute',
          top: '8px',
          right: '8px',
          width: '20px',
          height: '20px',
          color: '#666',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.05)',
          },
        }}
      >
        <CloseIcon sx={{ fontSize: '16px' }} />
      </IconButton>

      {/* Title Section */}
      <Box sx={{ marginBottom: '8px', width: '100%' }}>
        <Typography
          sx={{
            marginBottom: '4px',
            fontWeight: 'bold',
            fontSize: '11px',
            color: '#333',
          }}
        >
          入力内容
        </Typography>
        <TextField
          value={specInput}
          placeholder='入力ください'
          fullWidth
          size='small'
          InputProps={{ readOnly: true }}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#f9f9f9',
              fontSize: '11px',
              fontWeight: 'bold',
              color: '#333',
              height: '28px',
              '& fieldset': {
                borderColor: '#e0e0e0',
              },
            },
          }}
        />
      </Box>

      {/* Form Section */}
      <Box component='form' sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(9, 1fr)',
            gap: '4px',
            alignItems: 'start',
          }}
        >
          {/* Fc input */}
          <Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <Typography
                component='label'
                sx={{ fontSize: '9px', fontWeight: 'bold', color: '#000' }}
              >
                Fc
              </Typography>
              <TextField
                name='fcInput'
                value={form.fcInput}
                onChange={(e) => handleInputChange('fcInput', e.target.value, 'number')}
                onKeyDown={(e) => handleKeyDown(e, 'fcInput')}
                inputProps={{ maxLength: 6 }}
                size='small'
                sx={{
                  width: '60px',
                  '& .MuiOutlinedInput-root': {
                    height: '36px',
                    fontSize: '11px',
                    fontWeight: 'bold',
                    '& fieldset': {
                      borderColor: '#e0e0e0',
                    },
                    '&:hover fieldset': {
                      borderColor: '#1976d2',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#1976d2',
                    },
                  },
                }}
              />
            </Box>
          </Box>

          {/* S input with dropdown */}
          <Box>
            <InputWithDropdown
              name='sInput'
              label='S'
              value={form.sInput}
              options={['3', '6']}
              maxLength={4}
              onChange={(value) => handleInputChange('sInput', value, 'number')}
              onSelect={(value) => handleSelect('sInput', value)}
              onKeyDown={(e) => handleKeyDown(e, 'sInput')}
            />
          </Box>

          {/* Fm input */}
          <Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <Typography
                component='label'
                sx={{ fontSize: '9px', fontWeight: 'bold', color: '#000' }}
              >
                Fm
              </Typography>
              <TextField
                name='fmInput'
                value={form.fmInput}
                onChange={(e) => handleInputChange('fmInput', e.target.value, 'number')}
                onKeyDown={(e) => handleKeyDown(e, 'fmInput')}
                inputProps={{ maxLength: 6 }}
                size='small'
                sx={{
                  width: '60px',
                  '& .MuiOutlinedInput-root': {
                    height: '36px',
                    fontSize: '11px',
                    fontWeight: 'bold',
                    '& fieldset': {
                      borderColor: '#e0e0e0',
                    },
                    '&:hover fieldset': {
                      borderColor: '#1976d2',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#1976d2',
                    },
                  },
                }}
              />
            </Box>
          </Box>

          {/* SL/SF input with dropdown */}
          <Box>
            <InputWithDropdown
              name='slSfInput'
              label='SL/SF'
              value={form.slSfInput}
              options={SL_SF_ARR}
              maxLength={2}
              onChange={(value) => handleInputChange('slSfInput', value, 'number')}
              onSelect={(value) => handleSelect('slSfInput', value)}
              onKeyDown={(e) => handleKeyDown(e, 'slSfInput')}
            />
          </Box>

          {/* G input with dropdown */}
          <Box>
            <InputWithDropdown
              name='gInput'
              label='G'
              value={form.gInput}
              options={G_ARR}
              maxLength={2}
              onChange={(value) => handleInputChange('gInput', value, 'number')}
              onSelect={(value) => handleSelect('gInput', value)}
              onKeyDown={(e) => handleKeyDown(e, 'gInput')}
            />
          </Box>

          {/* Cement input with dropdown */}
          <Box>
            <InputWithDropdown
              name='cementInput'
              label='セメント'
              value={form.cementInput}
              options={CEMENT_ARR}
              maxLength={2}
              onChange={(value) => handleInputChange('cementInput', value, 'text')}
              onSelect={(value) => handleSelect('cementInput', value)}
              onKeyDown={(e) => handleKeyDown(e, 'cementInput')}
            />
          </Box>

          {/* AD radio buttons */}
          <Box>
            <RadioButtons
              label='AD'
              value={form.adRadio}
              options={['AE', 'SPC']}
              onChange={(value) => updateFormValue('adRadio', value)}
            />
          </Box>

          {/* Admixture input */}
          <Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <Typography
                component='label'
                sx={{ fontSize: '9px', fontWeight: 'bold', color: '#000' }}
              >
                混和材
              </Typography>
              <TextField
                name='admixtureInput'
                value={form.admixtureInput}
                onChange={(e) => handleInputChange('admixtureInput', e.target.value, 'text')}
                onKeyDown={(e) => handleKeyDown(e, 'admixtureInput')}
                inputProps={{ maxLength: 12 }}
                size='small'
                sx={{
                  width: '60px',
                  '& .MuiOutlinedInput-root': {
                    height: '36px',
                    fontSize: '11px',
                    fontWeight: 'bold',
                    '& fieldset': {
                      borderColor: '#e0e0e0',
                    },
                    '&:hover fieldset': {
                      borderColor: '#1976d2',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#1976d2',
                    },
                  },
                }}
              />
            </Box>
          </Box>

          {/* Misc input */}
          <Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <Typography
                component='label'
                sx={{ fontSize: '9px', fontWeight: 'bold', color: '#000' }}
              >
                その他
              </Typography>
              <TextField
                name='miscInput'
                value={form.miscInput}
                onChange={(e) => handleInputChange('miscInput', e.target.value, 'text')}
                onKeyDown={(e) => handleKeyDown(e, 'miscInput')}
                inputProps={{ maxLength: 12 }}
                size='small'
                sx={{
                  width: '60px',
                  '& .MuiOutlinedInput-root': {
                    height: '36px',
                    fontSize: '11px',
                    fontWeight: 'bold',
                    '& fieldset': {
                      borderColor: '#e0e0e0',
                    },
                    '&:hover fieldset': {
                      borderColor: '#1976d2',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#1976d2',
                    },
                  },
                }}
              />
            </Box>
          </Box>
        </Box>

        {/* Buttons */}
        <Box
          sx={{
            display: 'flex',
            gap: '6px',
            justifyContent: 'flex-end',
            marginTop: '8px',
            width: '100%',
          }}
        >
          <Button
            onClick={handleClear}
            variant='outlined'
            sx={{
              padding: '6px 16px',
              borderRadius: '15px',
              fontSize: '11px',
              fontWeight: 500,
              textTransform: 'none',
              borderColor: '#e0e0e0',
              color: '#333',
              '&:hover': {
                backgroundColor: '#f5f5f5',
                borderColor: '#d0d0d0',
              },
            }}
          >
            クリア
          </Button>
          <Button
            onClick={handleSubmit}
            variant='contained'
            sx={{
              padding: '6px 16px',
              borderRadius: '15px',
              fontSize: '11px',
              fontWeight: 500,
              textTransform: 'none',
              backgroundColor: '#1976d2',
              '&:hover': {
                backgroundColor: '#1565c0',
              },
            }}
          >
            決定
          </Button>
        </Box>
      </Box>
    </Box>,
    document.body
  );
};

/**
 * Input with dropdown component
 */
interface InputWithDropdownProps {
  name: string;
  label: string;
  value: string;
  options: string[];
  maxLength: number;
  onChange: (value: string) => void;
  onSelect: (value: string) => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
}

const InputWithDropdown: React.FC<InputWithDropdownProps> = ({
  name,
  label,
  value,
  options,
  maxLength,
  onChange,
  onSelect,
  onKeyDown,
}) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
      <Typography component='label' sx={{ fontSize: '9px', fontWeight: 'bold', color: '#000' }}>
        {label}
      </Typography>
      <TextField
        name={name}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        inputProps={{ maxLength }}
        size='small'
        sx={{
          width: '60px',
          '& .MuiOutlinedInput-root': {
            height: '36px',
            fontSize: '11px',
            fontWeight: 'bold',
            '& fieldset': {
              borderColor: '#e0e0e0',
            },
            '&:hover fieldset': {
              borderColor: '#1976d2',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#1976d2',
            },
          },
        }}
      />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', marginTop: '2px' }}>
        {options.map((option) => {
          const isSelected = value === option;
          return (
            <Button
              key={option}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onChange(option);
                onSelect(option);
              }}
              size='small'
              sx={{
                padding: '2px 8px',
                minWidth: '60px',
                height: '24px',
                fontSize: '9px',
                fontWeight: 'bold',
                textTransform: 'none',
                borderRadius: '8px',
                backgroundColor: isSelected ? '#1976d2' : 'white',
                color: isSelected ? 'white' : '#666',
                border: '1px solid',
                borderColor: isSelected ? '#1976d2' : '#e0e0e0',
                '&:hover': {
                  backgroundColor: isSelected ? '#1976d2' : '#f5f5f5',
                  borderColor: isSelected ? '#1976d2' : '#d0d0d0',
                },
              }}
            >
              {option}
            </Button>
          );
        })}
      </Box>
    </Box>
  );
};

/**
 * Radio buttons component
 */
interface RadioButtonsProps {
  label: string;
  value: 'AE' | 'SPC';
  options: ('AE' | 'SPC')[];
  onChange: (value: 'AE' | 'SPC') => void;
}

const RadioButtons: React.FC<RadioButtonsProps> = ({ label, value, options, onChange }) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
      <Typography component='label' sx={{ fontSize: '9px', fontWeight: 'bold', color: '#000' }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
        {options.map((option) => {
          const isSelected = value === option;
          return (
            <Button
              key={option}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onChange(option);
              }}
              size='small'
              sx={{
                padding: '2px 8px',
                minWidth: '60px',
                height: '36px',
                fontSize: '11px',
                fontWeight: 'bold',
                textTransform: 'none',
                borderRadius: '8px',
                backgroundColor: isSelected ? '#1976d2' : 'white',
                color: isSelected ? 'white' : '#666',
                border: '1px solid',
                borderColor: isSelected ? '#1976d2' : '#e0e0e0',
                '&:hover': {
                  backgroundColor: isSelected ? '#1976d2' : '#f5f5f5',
                  borderColor: isSelected ? '#1976d2' : '#d0d0d0',
                },
              }}
            >
              {option}
            </Button>
          );
        })}
      </Box>
    </Box>
  );
};

// Store active popup root to prevent multiple popups
let activePopupRoot: Root | null = null;

/** Keyboard dismiss + move row (1-line vs 3-line) when Enter without saving. */
export interface MerchandisePopupNavigationOptions {
  contentCol: number;
  formulationProductTypeCol: number;
  getNextMerchandiseRow: (row: number) => number;
  /** When set, auto-open on the next row only if this returns true (e.g. 3-line line 1). */
  isSelectableRow?: (row: number) => boolean;
}

interface ActiveMerchandiseSession {
  hot: Handsontable.Core;
  row: number;
  col: number;
  navigation: MerchandisePopupNavigationOptions;
  valueCommitted: boolean;
}

let activeMerchandiseSession: ActiveMerchandiseSession | null = null;
let merchandiseEnterDismissCleanup: (() => void) | null = null;

function markMerchandiseSessionCommitted(): void {
  if (activeMerchandiseSession) {
    activeMerchandiseSession.valueCommitted = true;
  }
}

function detachMerchandiseEnterDismissListener(): void {
  merchandiseEnterDismissCleanup?.();
  merchandiseEnterDismissCleanup = null;
}

/**
 * After closing the popup without saving: move selection to the next row and open the popup
 * again when 種類 is 生コン for that row.
 */
export function runMerchandiseDismissAndFocusNextRow(
  hot: Handsontable.Core,
  currentRow: number,
  _currentCol: number,
  nav: MerchandisePopupNavigationOptions
): void {
  const nextRow = nav.getNextMerchandiseRow(currentRow);
  const rowCount = typeof hot.countRows === 'function' ? hot.countRows() : 0;
  if (nextRow >= rowCount) {
    if (rowCount > 0) {
      hot.selectCell?.(rowCount - 1, nav.contentCol);
    }
    return;
  }

  hot.selectCell?.(nextRow, nav.contentCol);

  if (nav.isSelectableRow && !nav.isSelectableRow(nextRow)) {
    return;
  }

  const productType = hot.getDataAtCell?.(nextRow, nav.formulationProductTypeCol);
  if (productType !== EPlanFormulationProductTypeLabel.CONCRETE) {
    return;
  }

  const cellHtmlElement = hot.getCell?.(nextRow, nav.contentCol) as HTMLElement | null | undefined;
  if (!cellHtmlElement) {
    return;
  }

  globalThis.requestAnimationFrame(() => {
    globalThis.requestAnimationFrame(() => {
      openMerchandisePopup(hot, nextRow, nav.contentCol, cellHtmlElement, nav);
    });
  });
}

function attachMerchandiseEnterDismissListener(
  closeWithoutNavigate: () => void,
  getSessionSnapshot: () => ActiveMerchandiseSession | null
): void {
  detachMerchandiseEnterDismissListener();

  const onKeyDownCapture = (e: KeyboardEvent) => {
    if (e.key !== 'Enter') {
      return;
    }
    const session = getSessionSnapshot();
    if (!session || session.valueCommitted) {
      return;
    }

    const targetHtmlElement = e.target as HTMLElement | null;
    if (targetHtmlElement?.closest('.merchandise-popup')) {
      if (targetHtmlElement.closest('input, textarea')) {
        return;
      }
    }

    e.preventDefault();
    e.stopImmediatePropagation();

    const snapshot = { ...session, navigation: { ...session.navigation } };
    closeWithoutNavigate();

    globalThis.requestAnimationFrame(() => {
      globalThis.requestAnimationFrame(() => {
        runMerchandiseDismissAndFocusNextRow(
          snapshot.hot,
          snapshot.row,
          snapshot.col,
          snapshot.navigation
        );
      });
    });
  };

  document.addEventListener('keydown', onKeyDownCapture, true);
  merchandiseEnterDismissCleanup = () => {
    document.removeEventListener('keydown', onKeyDownCapture, true);
  };
}

export function isMerchandisePopupOpen(): boolean {
  return activePopupRoot !== null;
}

/**
 * Open merchandise popup for a cell
 * This function creates a React component and renders it via portal
 * @param instance - Handsontable instance
 * @param row - Row index
 * @param col - Column index
 * @param cell - Cell element
 * @param navigation - Row/column rules for Enter-dismiss then move to next 内容 row (3-line must pass explicit step).
 */
export function openMerchandisePopup(
  instance: Handsontable.Core,
  row: number,
  col: number,
  cell: HTMLElement,
  navigation?: MerchandisePopupNavigationOptions
) {
  // Close existing popup if any
  if (activePopupRoot) {
    activePopupRoot.unmount();
    activePopupRoot = null;
  }
  detachMerchandiseEnterDismissListener();
  activeMerchandiseSession = null;

  const nav: MerchandisePopupNavigationOptions =
    navigation ??
    ({
      contentCol: col,
      formulationProductTypeCol: 10,
      getNextMerchandiseRow: (r: number) => r + 1,
    } satisfies MerchandisePopupNavigationOptions);

  const currentValue = instance.getDataAtCell(row, col);

  // Create a container for the React component
  const container = document.createElement('div');
  document.body.appendChild(container);

  // Create root and render component
  const root = createRoot(container);
  activePopupRoot = root;

  activeMerchandiseSession = {
    hot: instance,
    row,
    col,
    navigation: nav,
    valueCommitted: false,
  };

  const handleClose = () => {
    detachMerchandiseEnterDismissListener();
    if (activePopupRoot) {
      activePopupRoot.unmount();
      activePopupRoot = null;
    }
    container.remove();
    activeMerchandiseSession = null;
  };

  attachMerchandiseEnterDismissListener(handleClose, () => activeMerchandiseSession);

  root.render(
    <MerchandisePopup
      instance={instance}
      row={row}
      col={col}
      cell={cell}
      value={currentValue || ''}
      onClose={handleClose}
    />
  );
}
